import type { UnityAdsPlugin } from "../types/unity-ads";
import type { GooglePlayBillingPlugin } from "../types/google-play-billing";

const ENABLE_ADS = (import.meta as any).env?.VITE_ENABLE_ADS === "true";
const STORAGE_KEY = "unity_ads_state_v1";
// Show ad after 3 completed games
const AD_THRESHOLD = 3;
// 2 minute cooldown between ads
const COOLDOWN_MS = 120 * 1000;
const REMOVE_ADS_PRODUCT_ID = "remove_ads";
const SYNC_RETRY_MAX = 3;
const SYNC_RETRY_DELAY_MS = 1000;

// Flag to prevent multiple simultaneous ad requests (race condition fix)
let showingAd = false;
let syncRetryCount = 0;
let syncRetryTimer: number | null = null;

type AdsState = {
  gamesCompletedSinceLastAd: number;
  adDue: boolean;
  lastAdShownAt: number;
  adsRemoved: boolean;
};

const defaultState: AdsState = {
  gamesCompletedSinceLastAd: 0,
  adDue: false,
  lastAdShownAt: 0,
  adsRemoved: false,
};

function isAndroidCapacitor(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const cap = (window as any).Capacitor;
  if (!cap || !cap.getPlatform) {
    return false;
  }
  return cap.getPlatform() === "android";
}

function isIOSCapacitor(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const cap = (window as any).Capacitor;
  if (!cap || !cap.getPlatform) {
    return false;
  }
  return cap.getPlatform() === "ios";
}

function isNativeApp(): boolean {
  return isAndroidCapacitor() || isIOSCapacitor();
}

function getUnityAds(): UnityAdsPlugin | null {
  if (typeof window === "undefined") {
    console.log("[UnityAds] getUnityAds: window is undefined");
    return null;
  }
  const cap = (window as any).Capacitor;
  if (!cap) {
    console.log("[UnityAds] getUnityAds: Capacitor not available");
    return null;
  }
  
  // Try multiple ways to access the plugin
  // Method 1: Direct access via Plugins object (try both UnityAds and UnityAdsPlugin)
  if (cap.Plugins?.UnityAds) {
    console.log("[UnityAds] getUnityAds: Found plugin in cap.Plugins.UnityAds");
    return cap.Plugins.UnityAds as UnityAdsPlugin;
  }
  if (cap.Plugins?.UnityAdsPlugin) {
    console.log("[UnityAds] getUnityAds: Found plugin in cap.Plugins.UnityAdsPlugin");
    return cap.Plugins.UnityAdsPlugin as UnityAdsPlugin;
  }
  
  // Method 2: Via getPlugin method (Capacitor 3+)
  if (cap.getPlugin) {
    try {
      const plugin = cap.getPlugin("UnityAds") as UnityAdsPlugin;
      if (plugin) {
        console.log("[UnityAds] getUnityAds: Found plugin via getPlugin");
        return plugin;
      }
    } catch (error) {
      console.log("[UnityAds] getUnityAds: Error getting plugin via getPlugin:", error);
    }
  }
  
  // Method 3: Try accessing via Plugins directly (alternative path)
  if ((cap as any).Plugins && (cap as any).Plugins.UnityAds) {
    console.log("[UnityAds] getUnityAds: Found plugin via alternative Plugins path");
    return (cap as any).Plugins.UnityAds as UnityAdsPlugin;
  }
  
  // Method 4: Try accessing via registerPlugin (if available)
  if (cap.registerPlugin) {
    try {
      const plugin = cap.registerPlugin("UnityAds") as UnityAdsPlugin;
      if (plugin) {
        console.log("[UnityAds] getUnityAds: Found plugin via registerPlugin");
        return plugin;
      }
    } catch (error) {
      console.log("[UnityAds] getUnityAds: Error getting plugin via registerPlugin:", error);
    }
  }
  
  // Debug: Log what's available
  console.log("[UnityAds] getUnityAds: Plugin not found. Available methods:", {
    hasPlugins: !!cap.Plugins,
    hasGetPlugin: !!cap.getPlugin,
    hasRegisterPlugin: !!cap.registerPlugin,
    pluginsKeys: cap.Plugins ? Object.keys(cap.Plugins) : [],
    capacitorVersion: cap.getPlatform ? "available" : "not available",
    allCapKeys: Object.keys(cap)
  });
  
  // Method 5: Try accessing via window.Capacitor.Plugins directly
  if ((window as any).Capacitor?.Plugins?.UnityAds) {
    console.log("[UnityAds] getUnityAds: Found plugin via window.Capacitor.Plugins.UnityAds");
    return (window as any).Capacitor.Plugins.UnityAds as UnityAdsPlugin;
  }
  
  // Method 6: Try using withPlugin (if available)
  if (cap.withPlugin) {
    try {
      const plugin = cap.withPlugin("UnityAds") as UnityAdsPlugin;
      if (plugin) {
        console.log("[UnityAds] getUnityAds: Found plugin via withPlugin");
        return plugin;
      }
    } catch (error) {
      console.log("[UnityAds] getUnityAds: Error getting plugin via withPlugin:", error);
    }
  }
  
  // Method 7: Try checking if plugin is available and accessing it
  if (cap.isPluginAvailable && cap.isPluginAvailable("UnityAds")) {
    console.log("[UnityAds] getUnityAds: Plugin is available, trying to access...");
    // Try direct access
    if ((cap as any).Plugins?.UnityAds) {
      return (cap as any).Plugins.UnityAds as UnityAdsPlugin;
    }
  }
  
  return null;
}

function getGooglePlayBilling(): GooglePlayBillingPlugin | null {
  if (typeof window === "undefined") {
    return null;
  }
  const cap = (window as any).Capacitor;
  if (!cap) {
    return null;
  }
  if (cap.Plugins?.GooglePlayBilling) {
    return cap.Plugins.GooglePlayBilling as GooglePlayBillingPlugin;
  }
  if (cap.getPlugin) {
    try {
      return cap.getPlugin("GooglePlayBilling") as GooglePlayBillingPlugin;
    } catch {
      return null;
    }
  }
  return null;
}

function readState(): AdsState {
  if (typeof window === "undefined") {
    return { ...defaultState };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ...defaultState };
    }
    const parsed = JSON.parse(raw);
    return {
      gamesCompletedSinceLastAd: Number(parsed.gamesCompletedSinceLastAd) || 0,
      adDue: Boolean(parsed.adDue),
      lastAdShownAt: Number(parsed.lastAdShownAt) || 0,
      adsRemoved: Boolean(parsed.adsRemoved),
    };
  } catch {
    return { ...defaultState };
  }
}

function writeState(state: AdsState): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export function isAdsRemoved(): boolean {
  return readState().adsRemoved;
}

export function isUnityAdShowing(): boolean {
  return showingAd;
}

export function setAdsRemoved(removed: boolean): void {
  const state = readState();
  state.adsRemoved = removed;
  if (removed) {
    state.gamesCompletedSinceLastAd = 0;
    state.adDue = false;
  }
  writeState(state);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("adsRemovedChanged", { detail: { adsRemoved: removed } }));
  }
}

export async function syncAdsRemovedFromBilling(): Promise<boolean | null> {
  if (!ENABLE_ADS || !isAndroidCapacitor()) {
    // iOS billing integration will be added later when Apple developer account is approved
    return null;
  }
  const billing = getGooglePlayBilling();
  if (!billing) {
    scheduleSyncRetry();
    return null;
  }
  try {
    const result = await billing.queryPurchases();
    const owned = Array.isArray(result?.productIds) && result.productIds.includes(REMOVE_ADS_PRODUCT_ID);
    setAdsRemoved(owned);
    syncRetryCount = 0;
    return owned;
  } catch {
    scheduleSyncRetry();
    return null;
  }
}

function scheduleSyncRetry(): void {
  if (typeof window === "undefined") {
    return;
  }
  if (syncRetryCount >= SYNC_RETRY_MAX) {
    return;
  }
  if (syncRetryTimer) {
    return;
  }
  syncRetryCount += 1;
  syncRetryTimer = window.setTimeout(() => {
    syncRetryTimer = null;
    void syncAdsRemovedFromBilling();
  }, SYNC_RETRY_DELAY_MS * syncRetryCount);
}

export function recordGameCompleted(): void {
  if (!ENABLE_ADS || !isNativeApp()) {
    return;
  }
  const state = readState();
  if (state.adsRemoved) {
    return;
  }
  
  // If ad is due, don't increment counter (wait for ad to be shown)
  // But add safety check: if adDue has been true for too long (stuck state),
  // clear it to prevent getting stuck (both platforms)
  if (state.adDue) {
    // Safety check for stuck state (applies to both platforms)
    const timeSinceLastAd = state.lastAdShownAt ? Date.now() - state.lastAdShownAt : Infinity;
    if (timeSinceLastAd > COOLDOWN_MS * 10) {
      // Ad has been due for way too long (20+ minutes), likely stuck - reset it
      const platform = isIOSCapacitor() ? "iOS" : "Android";
      console.warn(`[UnityAds] ${platform}: adDue stuck for too long, resetting state`);
      state.adDue = false;
      state.gamesCompletedSinceLastAd = 0;
      writeState(state);
    }
    return;
  }
  
  state.gamesCompletedSinceLastAd += 1;
  if (state.gamesCompletedSinceLastAd >= AD_THRESHOLD) {
    state.gamesCompletedSinceLastAd = 0;
    state.adDue = true;
    if (isIOSCapacitor()) {
      console.log("[UnityAds] iOS: Ad threshold reached, adDue set to true");
    }
  }
  writeState(state);
}

export async function maybeShowAdBeforeMatchmaking(): Promise<{ shown: boolean; reason?: string }> {
  console.log("[UnityAds] maybeShowAdBeforeMatchmaking called, ENABLE_ADS:", ENABLE_ADS);
  
  if (!ENABLE_ADS) {
    console.log("[UnityAds] Ads disabled - VITE_ENABLE_ADS not set");
    return { shown: false, reason: "disabled" };
  }
  if (!isNativeApp()) {
    console.log("[UnityAds] Not native app (Android or iOS)");
    return { shown: false, reason: "not_native" };
  }

  // Prevent multiple simultaneous ad requests (race condition fix)
  if (showingAd) {
    console.log("[UnityAds] Already showing an ad, skipping duplicate request");
    return { shown: false, reason: "already_showing" };
  }

  const state = readState();
  console.log("[UnityAds] Current state:", state);
  
  if (state.adsRemoved) {
    state.adDue = false;
    state.gamesCompletedSinceLastAd = 0;
    writeState(state);
    console.log("[UnityAds] Ads removed by user");
    return { shown: false, reason: "ads_removed" };
  }
  
  if (!state.adDue) {
    console.log("[UnityAds] Ad not due yet");
    return { shown: false, reason: "not_due" };
  }
  
  if (state.lastAdShownAt && Date.now() - state.lastAdShownAt < COOLDOWN_MS) {
    const timeSince = Date.now() - state.lastAdShownAt;
    console.log("[UnityAds] Cooldown active, time since last ad:", timeSince, "ms");
    return { shown: false, reason: "cooldown" };
  }

  const plugin = getUnityAds();
  if (!plugin) {
    console.error("[UnityAds] Plugin not available");
    return { shown: false, reason: "plugin_unavailable" };
  }

  // CRITICAL: Clear adDue IMMEDIATELY before showing ad to prevent race conditions
  // This ensures only one ad can be triggered even if function is called multiple times
  state.adDue = false;
  writeState(state);
  showingAd = true;

  console.log("[UnityAds] Calling plugin.maybeShowRewardedAd()...");
  try {
    const result = await plugin.maybeShowRewardedAd();
    console.log("[UnityAds] Plugin result:", result);
    
    // Always clear the showing flag when done (success or failure)
    showingAd = false;
    
    if (result?.shown) {
      // Ad shown successfully - update timestamp and ensure adDue stays false
      // Re-read state to ensure we have the latest (adDue was already cleared above)
      // This ensures state consistency across both platforms
      const currentState = readState();
      currentState.lastAdShownAt = Date.now();
      currentState.adDue = false; // Ensure it's still false
      currentState.gamesCompletedSinceLastAd = 0; // Reset counter after ad shown (safety measure)
      writeState(currentState);
      if (isIOSCapacitor()) {
        console.log("[UnityAds] iOS: Ad shown successfully, state updated:", currentState);
      } else {
        console.log("[UnityAds] Android: Ad shown successfully, state updated:", currentState);
      }
      return { shown: true };
    }
    
    // Ad didn't show - check if it's a permanent failure or temporary
    const reason = result?.reason || "not_ready";
    const permanentFailures = ["show_failed", "timeout", "ad_skipped"];
    
    if (permanentFailures.includes(reason)) {
      console.log("[UnityAds] Ad failed permanently. Reason:", reason);
      // Re-read and save state to ensure consistency (both platforms)
      const currentState = readState();
      currentState.adDue = false; // Ensure it stays cleared
      writeState(currentState);
      // adDue already cleared above, so user won't get stuck
    } else {
      // Temporary failure (not_ready) - restore adDue so it can retry later
      // But respect cooldown - don't restore if we're still in cooldown period
      const currentState = readState();
      const timeSinceLastAd = currentState.lastAdShownAt ? Date.now() - currentState.lastAdShownAt : Infinity;
      if (timeSinceLastAd >= COOLDOWN_MS) {
        currentState.adDue = true;
        writeState(currentState);
        const platform = isIOSCapacitor() ? "iOS" : "Android";
        console.log(`[UnityAds] ${platform}: Ad not ready, will retry later (cooldown passed)`);
      } else {
        currentState.adDue = false;
        writeState(currentState);
        const platform = isIOSCapacitor() ? "iOS" : "Android";
        console.log(`[UnityAds] ${platform}: Ad not ready, but cooldown still active, not restoring adDue`);
      }
    }
    
    console.log("[UnityAds] Ad not shown, reason:", reason);
    return { shown: false, reason };
  } catch (error) {
    console.error("[UnityAds] Error calling plugin:", error);
    console.error("[UnityAds] Error stringified:", JSON.stringify(error));
    console.error("[UnityAds] Error type:", typeof error);
    if (error && typeof error === "object") {
      console.error("[UnityAds] Error keys:", Object.keys(error));
      console.error("[UnityAds] Error message:", (error as any).message);
      console.error("[UnityAds] Error stack:", (error as any).stack);
    }
    // Always clear the showing flag on error
    showingAd = false;
    // On error, don't restore adDue - let it clear so user doesn't get stuck
    return { shown: false, reason: "error" };
  }
}
